import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { authApi } from '../../api/endpoints.js';
import { saveTokens, clearSession, TOKEN_KEYS, errorMessage } from '../../api/axios.js';
import { DEMO_TOKEN } from '../../demo/adapter.js';
import { saveMarket, resolveMarket } from '../../demo/market.js';
import { DEFAULT_MARKET } from '../../demo/markets.js';

/**
 * Аутентификация (ТЗ 11.14 — логин + пароль, каттоо жок).
 *
 * `status` баштапкы абалы 'idle' — биринчи рендерде токен текшериле элек,
 * ал mount'тан кийин аныкталат.
 *
 * Демо-режим (сатуу көрсөтмөсү үчүн): токен `DEMO_TOKEN` болсо серверге
 * такыр кайрылбайбыз — жоопту `demo/adapter.js` берет, колдонуучуну да.
 * Ошондуктан бул жерде демо үчүн өзүнчө тармак дээрлик жок. Кайсы базар
 * экени `demo/market.js`те сакталат.
 */

export const login = createAsyncThunk('auth/login', async ({ login: loginName, password }, { rejectWithValue }) => {
  try {
    const data = await authApi.login(loginName, password);
    saveTokens(data);
    return data.user;
  } catch (err) {
    return rejectWithValue(errorMessage(err, 'Не удалось войти'));
  }
});

export const loginDemo = createAsyncThunk('auth/loginDemo', async (market) => {
  saveMarket(resolveMarket(market) ?? DEFAULT_MARKET);
  saveTokens({ accessToken: DEMO_TOKEN, refreshToken: 'demo-refresh' });
  // Колдонуучуну адаптер берет — аты ошол базардын тилинде
  return authApi.me();
});

export const fetchMe = createAsyncThunk('auth/fetchMe', async (_, { rejectWithValue }) => {
  try {
    return await authApi.me();
  } catch (err) {
    return rejectWithValue(errorMessage(err));
  }
});

/**
 * Интерфейс тандоосу (тема, тексттин өлчөмү) — дароо колдонулат, андан кийин
 * серверге сакталат: кайсы компьютерден кирсе да ошол жөндөө ачылат.
 */
export const updatePrefs = createAsyncThunk('auth/updatePrefs', async (patch, { rejectWithValue, getState }) => {
  const token = localStorage.getItem(TOKEN_KEYS.ACCESS);
  // Демодо бар жөндөөлөрдүн үстүнө кошобуз: алмаштырсак, теманы өзгөрткөндө
  // тексттин өлчөмү демейкиге кайтып кетмек
  if (token === DEMO_TOKEN) return { prefs: { ...(getState().auth.user?.prefs ?? {}), ...patch } };
  try {
    return await authApi.updatePrefs(patch);
  } catch (err) {
    return rejectWithValue(errorMessage(err));
  }
});

export const logout = createAsyncThunk('auth/logout', async () => {
  const token = localStorage.getItem(TOKEN_KEYS.ACCESS);
  if (token !== DEMO_TOKEN) {
    const refreshToken = localStorage.getItem(TOKEN_KEYS.REFRESH);
    // Сервер жетпесе да сессия жергиликтүү бүтүшү керек
    await authApi.logout(refreshToken).catch(() => {});
  }
  clearSession();
});

const authSlice = createSlice({
  name: 'auth',
  initialState: { user: null, status: 'idle', error: null },
  reducers: {
    /** Токен жок экени белгилүү болду — кирүү керек */
    markAnonymous(state) {
      state.status = 'anonymous';
      state.user = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => { state.status = 'loading'; state.error = null; })
      .addCase(login.fulfilled, (state, { payload }) => { state.status = 'ready'; state.user = payload; })
      .addCase(login.rejected, (state, { payload }) => { state.status = 'anonymous'; state.error = payload; })

      .addCase(loginDemo.fulfilled, (state, { payload }) => { state.status = 'ready'; state.user = payload; })

      .addCase(fetchMe.pending, (state) => { state.status = 'loading'; })
      .addCase(fetchMe.fulfilled, (state, { payload }) => { state.status = 'ready'; state.user = payload; })
      .addCase(fetchMe.rejected, (state) => { state.status = 'anonymous'; state.user = null; })

      .addCase(updatePrefs.pending, (state, { meta }) => {
        if (state.user) state.user.prefs = { ...(state.user.prefs ?? {}), ...meta.arg };
      })
      .addCase(updatePrefs.fulfilled, (state, { payload }) => { if (state.user) state.user.prefs = payload.prefs; })

      .addCase(logout.fulfilled, (state) => { state.status = 'anonymous'; state.user = null; });
  },
});

export const { markAnonymous } = authSlice.actions;
export default authSlice.reducer;
